import { useDispatch } from 'react-redux';

import { ERROR_TYPES } from '@api/ecommerce-xp';
import {
	useAutoFetch as useAutoFetchCore,
	UseAutoFetchInput,
	UseAutoFetchHookOutput,
	useShowHide,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { EnginesCatalog } from '@bcp-nextgen-dx-touchpoint-framework/react-js';
import { GLOBAL_LOADER } from '@components';
import { BusinessOperationKeys, TBusinessOperationKeys } from '@configs';
import { useLogger } from '@hooks';
import { useNavigation } from '@navigation';
import { saveErrorServiceResponse, usePTCMetadata } from '@store';
import { Anything } from '@types';

export const useAutoFetch = <TInput, TOutput>(
	input: Omit<UseAutoFetchInput<TInput, TOutput>, 'fetchFn' | 'options'> & {
		operationKey: TBusinessOperationKeys;
		withLoading?: boolean;
	}
): UseAutoFetchHookOutput<TOutput> => {
	const { data, cache, operationKey, onSuccess, onError, withLoading = true } = input;

	const metadata = usePTCMetadata();
	const logger = useLogger();
	const { navigate } = useNavigation();
	const dispatch = useDispatch();

	const { hide, show } = useShowHide(GLOBAL_LOADER);

	return useAutoFetchCore<TInput, TOutput>({
		data,
		cache,
		// eslint-disable-next-line sonarjs/cognitive-complexity
		fetchFn: (dataInput?: TInput) => {
			logger(`useFetch ${operationKey} > request`, { dataInput });

			return new Promise((resolve, reject) => {
				EnginesCatalog.getInstance(EnginesCatalog)
					.getCommunicationEngine()
					.request(
						{
							operationKey: BusinessOperationKeys[operationKey],
						},
						{
							data: { ...dataInput, metadata },
							metadata: {
								messageId: '',
								messageCreatorId: '',
								messageCreationTimestampUTC: new Date(),
								correlationId: '',
								distributedTracingContext: '',
								authenticationValue: '',
								validateOnly: false,
								languageCode: '',
							},
						},
						true,
						undefined,
						true
					)
					.then((jsonData: Anything) => {
						resolve(jsonData);
					})
					.catch((err) => {
						if (Array.isArray(err)) {
							reject({ type: ERROR_TYPES.BUSINESS, errors: err });
						} else if (err instanceof Error) {
							const responseError = {
								type: ERROR_TYPES.SERVICE,
								errors: [{ message: 'We were unable to satisfy your request' }],
							};
							reject(responseError);
						} else {
							reject(err);
						}
					});
			});
		},
		onError: (err: Anything) => {
			if (onError) {
				onError(err);
			} else {
				dispatch(saveErrorServiceResponse(err));
				navigate('serviceError');
			}
		},
		onSuccess,
		options: withLoading
			? {
					loadingFunctions: {
						hide,
						show,
					},
			  }
			: undefined,
	});
};
